import { folderPath, normalizeTags } from "@hq/core";
import { useFolders, useNote, useNowContext, useServices } from "@hq/client-react";
import {
  useDeleteConfirmation,
  Badge,
  Button,
  cn,
  ContextMenu,
  DropdownMenu,
  IconButton,
  TagInput,
  toast,
} from "@z/ui";
import {
  ChevronRight,
  Copy,
  Download,
  Ellipsis,
  FolderInput,
  Pencil,
  Pin,
  PinOff,
  Plus,
  Trash2,
} from "lucide-react";
import { type FocusEvent, type ReactNode, useRef, useState } from "react";
import { downloadBlob, noteFileContents, noteFileName } from "./export";
import { FolderPickerDialog } from "./FolderPickerDialog";
import { displayTitle, effectiveFolderId, formatDateTime, relativeTime } from "./lib";
import { normalizeTag, useAllTags } from "./tags";

export type NoteHeaderProps = {
  noteId: string;
  /** Breadcrumb clicks; `null` is the root. */
  onSelectFolder?: (folderId: string | null) => void;
  /** Called after the note is moved to Trash from the menu. */
  onDeleted?: (noteId: string) => void;
  /** Makes the tag badges clickable, e.g. to filter the list by that tag. */
  onTagClick?: (tag: string) => void;
  /** Enter commits the title, then moves focus into the note's editor. */
  onEnterContent?: () => void;
  /** Rendered on the right, e.g. the editor's save status. */
  children?: ReactNode;
  className?: string;
  /** Places note navigation and actions in surrounding editor chrome. */
  renderLayout?: (parts: {
    header: ReactNode;
    breadcrumb: ReactNode;
    actions: ReactNode;
  }) => ReactNode;
};

/**
 * The note's tags as quiet badges, or a chip editor once the area is clicked. Each chip added
 * or removed writes through `notes.setTags` at once; typing never writes. Leaving the editor
 * (focus moves out, or Escape on an empty box) returns to the badges.
 */
function TagsRow({
  tags,
  onChange,
  onTagClick,
}: {
  tags: readonly string[];
  onChange: (next: string[]) => void;
  onTagClick?: ((tag: string) => void) | undefined;
}) {
  const [editing, setEditing] = useState(false);
  const all = useAllTags();

  if (editing) {
    const onBlur = (event: FocusEvent<HTMLDivElement>) => {
      const next = event.relatedTarget;
      if (!(next instanceof Node) || !event.currentTarget.contains(next)) setEditing(false);
    };
    return (
      <div
        data-slot="note-tags"
        data-editing=""
        className="max-w-md"
        onBlur={onBlur}
        onKeyDown={(event) => {
          if (event.key === "Escape") {
            event.preventDefault();
            setEditing(false);
          }
        }}
      >
        <TagInput
          aria-label="Tags"
          autoFocus
          size="sm"
          placeholder="Add a tag…"
          value={tags}
          suggestions={all.map((t) => t.tag)}
          normalize={normalizeTag}
          onChange={onChange}
        />
      </div>
    );
  }

  return (
    <div data-slot="note-tags" className="flex flex-wrap items-center gap-1">
      {tags.map((tag) =>
        onTagClick ? (
          <button
            key={tag}
            type="button"
            data-slot="note-tag"
            title={`Filter by ${tag}`}
            onClick={() => onTagClick(tag)}
            className="inline-flex h-5 items-center rounded-sm bg-surface-raised px-1.5 text-xs text-fg-muted outline-none hover:bg-fg/8 hover:text-fg focus-ring"
          >
            {tag}
          </button>
        ) : (
          <Badge
            key={tag}
            data-slot="note-tag"
            className="border-transparent bg-surface-raised font-normal text-fg-muted"
          >
            {tag}
          </Badge>
        ),
      )}
      <Button
        variant="ghost"
        size="sm"
        onClick={() => setEditing(true)}
        className="h-6 gap-1 px-1 text-xs font-normal text-fg-faint hover:text-fg"
      >
        <Plus className="size-3" />
        {tags.length === 0 ? "Add tag" : "Edit tags"}
      </Button>
    </div>
  );
}

/**
 * Title, folder breadcrumb, tags, metadata and the note's action menu. The title edits
 * inline: Enter or blur renames, Escape reverts.
 */
export function NoteHeader({
  noteId,
  onSelectFolder,
  onDeleted,
  onTagClick,
  onEnterContent,
  renderLayout,
  children,
  className,
}: NoteHeaderProps) {
  const services = useServices();
  const { requestDelete, deleteDialog } = useDeleteConfirmation();
  const now = useNowContext();
  const note = useNote(noteId);
  const folders = useFolders().data ?? [];
  const [draft, setDraft] = useState<string | null>(null);
  const [moving, setMoving] = useState(false);
  const skipBlurCommit = useRef(false);
  const titleRef = useRef<HTMLTextAreaElement>(null);

  const current = note.data;
  if (!current) {
    return note.isLoading ? null : (
      <header className={cn("px-4 py-3 text-sm text-fg-muted", className)}>Note not found.</header>
    );
  }
  const folderId = effectiveFolderId(current, folders);
  const path = folderId ? folderPath(folders, folderId) : [];

  const commitTitle = async () => {
    if (draft === null) return;
    setDraft(null);
    if (draft.trim() === current.title) return;
    try {
      await services.notes.rename(current.id, draft);
    } catch (err) {
      toast({
        title: "Could not rename note",
        description: err instanceof Error ? err.message : String(err),
        variant: "danger",
      });
    }
  };

  const copyMarkdown = async () => {
    try {
      await navigator.clipboard.writeText(current.markdown);
      toast({ title: "Copied as Markdown", variant: "success" });
    } catch {
      toast({ title: "Could not access the clipboard", variant: "danger" });
    }
  };

  const exportMarkdown = () => {
    downloadBlob(
      noteFileName(current),
      new Blob([noteFileContents(current, folders)], { type: "text/markdown;charset=utf-8" }),
    );
  };

  const trash = () => {
    requestDelete({
      name: displayTitle(current),
      description: "You can restore this file from Trash.",
      onConfirm: async () => {
        await services.notes.delete(current.id);
        onDeleted?.(current.id);
        toast({
          title: `Moved "${displayTitle(current)}" to Trash`,
          action: { label: "Undo", onClick: () => void services.notes.restore(current.id) },
        });
      },
    });
  };

  const crumbClass =
    "max-w-40 truncate rounded-sm text-xs text-fg-muted outline-none hover:text-fg focus-ring";

  const actions = (
    <>
      <DropdownMenu.Item
        onClick={() => {
          requestAnimationFrame(() => {
            titleRef.current?.focus();
            titleRef.current?.select();
          });
        }}
      >
        <Pencil />
        Rename
      </DropdownMenu.Item>
      <DropdownMenu.Item onClick={() => void services.notes.setPinned(current.id, !current.pinned)}>
        {current.pinned ? <PinOff /> : <Pin />}
        {current.pinned ? "Unpin" : "Pin"}
      </DropdownMenu.Item>
      <DropdownMenu.Item onClick={() => setMoving(true)}>
        <FolderInput /> Move to folder…
      </DropdownMenu.Item>
      <DropdownMenu.Separator />
      <DropdownMenu.Item onClick={() => void copyMarkdown()}>
        <Copy /> Copy as Markdown
      </DropdownMenu.Item>
      <DropdownMenu.Item onClick={exportMarkdown}>
        <Download /> Export as .md
      </DropdownMenu.Item>
      <DropdownMenu.Separator />
      <DropdownMenu.Item variant="destructive" onClick={() => void trash()}>
        <Trash2 /> Delete
      </DropdownMenu.Item>
      <DropdownMenu.Separator />
      <DropdownMenu.Label className="flex flex-col gap-1 font-normal normal-case tracking-normal">
        <span>Created {formatDateTime(current.createdAt, now.timeZone)}</span>
        <span>Updated {formatDateTime(current.updatedAt, now.timeZone)}</span>
      </DropdownMenu.Label>
    </>
  );

  const breadcrumb = (
    <nav
      aria-label="Folder"
      className={cn("flex min-w-0 items-center gap-1", renderLayout ? "max-w-40" : "flex-1")}
    >
      <button type="button" className={crumbClass} onClick={() => onSelectFolder?.(null)}>
        Notes
      </button>
      {path.map((folder) => (
        <span key={folder.id} className="flex min-w-0 items-center gap-1">
          <ChevronRight className="size-3 shrink-0 text-fg-faint" />
          <button type="button" className={crumbClass} onClick={() => onSelectFolder?.(folder.id)}>
            {folder.name}
          </button>
        </span>
      ))}
    </nav>
  );
  const toolbarActions = (
    <div className="flex shrink-0 items-center gap-2 whitespace-nowrap">
      {children}
      <DropdownMenu.Root>
        <DropdownMenu.Trigger
          render={
            <IconButton label="Note actions" size="sm">
              <Ellipsis />
            </IconButton>
          }
        />
        <DropdownMenu.Content align="end" finalFocus={false}>
          {actions}
        </DropdownMenu.Content>
      </DropdownMenu.Root>
    </div>
  );
  const header = (
    <ContextMenu.Trigger
      render={
        <header
          className={cn(
            "flex flex-col gap-3 px-6 pt-5 pb-4 @md/note:px-12 @md/note:pt-6",
            className,
          )}
        >
          {!renderLayout && (
            <div className="flex items-center gap-2">
              {breadcrumb}
              {toolbarActions}
            </div>
          )}

          <textarea
            rows={1}
            ref={titleRef}
            aria-label="Note title"
            value={draft ?? (current.title === "Untitled" ? "" : current.title)}
            placeholder="Untitled"
            className="w-full min-w-0 resize-none appearance-none overflow-hidden bg-transparent px-0 pb-1 text-[30px] leading-[1.2] font-semibold tracking-[-0.035em] text-fg placeholder:text-fg-faint focus-underline [field-sizing:content] @md/note:text-[38px]"
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.nativeEvent.isComposing || e.keyCode === 229) return;
              if (e.key === "Enter") {
                e.preventDefault();
                skipBlurCommit.current = true;
                void commitTitle();
                e.currentTarget.blur();
                onEnterContent?.();
              } else if (e.key === "Escape") {
                e.preventDefault();
                skipBlurCommit.current = true;
                setDraft(null);
                e.currentTarget.blur();
              }
            }}
            onBlur={() => {
              if (skipBlurCommit.current) {
                skipBlurCommit.current = false;
                return;
              }
              void commitTitle();
            }}
          />

          <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
            <TagsRow
              tags={current.tags ?? []}
              onTagClick={onTagClick}
              onChange={(next) => void services.notes.setTags(current.id, normalizeTags(next))}
            />
            <span
              className="text-xs text-fg-faint"
              title={formatDateTime(current.updatedAt, now.timeZone)}
            >
              Edited {relativeTime(current.updatedAt, now)}
            </span>
          </div>

          <FolderPickerDialog
            open={moving}
            onOpenChange={setMoving}
            title={`Move "${displayTitle(current)}"`}
            currentId={folderId}
            onPick={(id) => void services.notes.move(current.id, id)}
          />
        </header>
      }
    />
  );

  return (
    <ContextMenu.Root>
      {deleteDialog}
      {renderLayout ? renderLayout({ header, breadcrumb, actions: toolbarActions }) : header}
      <ContextMenu.Content aria-label="Note actions" finalFocus={false}>
        {actions}
      </ContextMenu.Content>
    </ContextMenu.Root>
  );
}
